import React, { useState, useEffect } from 'react';
import { Button, Box, Modal, Typography, Chip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useSearchParams } from 'react-router-dom';
import apiRequest from '../../../../../services/api/handler';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material';
import { useAuth } from '../../../../../services/auth/AuthContext';

// The orderings the backend allows. Anything not on this list is a 400 from
// /reseller/applications/referral-code, so the UI and the allowlist in
// reseller/serializers/applications.py move together.
const SORT_OPTIONS = [
    { value: 'default', label: 'Default' },
    { value: 'created_at_desc', label: 'Newest enquiries first' },
    { value: 'created_at_asc', label: 'Oldest enquiries first' },
    { value: 'last_activity_at_desc', label: 'Most recent activity first' },
    { value: 'last_activity_at_asc', label: 'Least recent activity first' },
];

const SUPPORT_SORT_OPTIONS = [
    { value: 'last_call_time_desc', label: 'Last Call Desc' },
    { value: 'last_call_time_asc', label: 'Last Call Asc' },
];

const FilterModal = ({
    open,
    onClose,
    filters,
    onFilterChange,
    onApplyFilters,
    onResetFilters,
    isSupport,
    isPartner,
    displaySort,
    displayRejectionReason,
}) => {
    const [filterOptions, setFilterOptions] = useState({});

    const theme = useTheme();

    useEffect(() => {
        apiRequest('/reseller/filters', 'GET', null, true)
            .then((response) => {
                setFilterOptions(response);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
                borderRadius: 0, // Set border radius to 0
            },
            sx: {
                '& .Mui-selected': {
                    borderRadius: 0,
                    backgroundColor: theme.palette.primary.main + ' !important',
                    color: "#fff !important",
                    fontWeight: 'bold'
                },
                '& .Mui-selected:hover': {
                    borderRadius: 0,
                    backgroundColor: theme.palette.primary.light + ' !important',
                    color: "#fff !important",
                    fontWeight: 'bold'
                }
            }
        },
    };

    // Generic handler for multi-select filters
    const handleMultiSelectChange = (field, event) => {
        const value = event.target.value;

        // Find the last changed item (added or removed)
        const lastItem = value.find(item => !filters[field].includes(item)) ||
                        filters[field].find(item => !value.includes(item));

        if (lastItem === 'all') {
            // If "All" was clicked
            if (value.includes('all')) {
                onFilterChange(field, ['all']);
            } else {
                onFilterChange(field, []);
            }
        } else {
            // If a specific option was clicked
            if (filters[field].includes('all')) {
                onFilterChange(field, [lastItem]);
            } else {
                onFilterChange(field, value);
            }
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '80vh',
                    overflow: 'auto'
                }}
            >
                <Typography variant="h6" component="h2">
                    Apply Filters
                </Typography>
                
                {isSupport && (
                    <>
                        {/* Source Filter */}
                        <Box mt={2}>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <InputLabel id="source-label">Source</InputLabel>
                                <Select
                                    labelId="source-label"
                                    id="source"
                                    multiple
                                value={filters.source}
                                    onChange={(e) => handleMultiSelectChange("source", e)}
                                    input={<OutlinedInput label="Source" />}
                                    MenuProps={MenuProps}
                            >
                                <MenuItem value="all">All</MenuItem>
                                    {filterOptions['sources']?.map((source) => (
                                        <MenuItem key={source.id} value={source.id}>{source.source_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        
                        {/* Campaign Filter */}
                        <Box mt={2}>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <InputLabel id="campaign-label">Campaign</InputLabel>
                                <Select
                                    labelId="campaign-label"
                                    id="campaign"
                                    multiple
                                value={filters.campaign}
                                    onChange={(e) => handleMultiSelectChange("campaign", e)}
                                    input={<OutlinedInput label="Campaign" />}
                                    MenuProps={MenuProps}
                            >
                                <MenuItem value="all">All</MenuItem>
                                    {filterOptions['campaigns']?.map((campaign) => (
                                        <MenuItem key={campaign.id} value={campaign.id}>{campaign.campaign_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </>
                )}
                
                {/* States Filter */}
                <Box mt={2}>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel id="state-label">States</InputLabel>
                        <Select
                            labelId="state-label"
                            id="state"
                            multiple
                        value={filters.state}
                            onChange={(e) => handleMultiSelectChange("state", e)}
                            input={<OutlinedInput label="States" />}
                            MenuProps={MenuProps}
                    >
                        <MenuItem value="all">All</MenuItem>
                            {filterOptions['states']?.map((state) => (
                                <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                
                {!isPartner && (
                    <>
                    {/* Priority Filter */}
                    <Box mt={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="priority-label">Priority</InputLabel>
                            <Select
                                labelId="priority-label"
                                id="priority"
                                multiple
                            value={filters.priority}
                                onChange={(e) => handleMultiSelectChange("priority", e)}
                                input={<OutlinedInput label="Priority" />}
                                MenuProps={MenuProps}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="high">High</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="low">Low</MenuItem>
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="not_interested">Not Interested</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Disposition Filter - Keep as is */}
                    <Box mt={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="disposition">Disposition</InputLabel>
                            <Select
                                labelId="disposition"
                                id="disposition"
                                multiple
                                value={filters.dispositions}
                                onChange={(e) => handleMultiSelectChange("dispositions", e)}
                                input={<OutlinedInput label="Disposition" />}
                                MenuProps={MenuProps}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="default">Default/No Calls Made</MenuItem>
                                {filterOptions['dispositions']?.map((disposition) => (
                                    <MenuItem key={disposition.value} value={disposition.label}>
                                        {disposition.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    </>
                )}

                {/* Sort Filter */}
                {displaySort && (
                    <Box mt={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="sort-label">Sort By</InputLabel>
                            <Select
                                labelId="sort-label"
                                id="sort"
                            value={filters.sort}
                            onChange={(e) => onFilterChange("sort", e.target.value)}
                                input={<OutlinedInput label="Sort By" />}
                        >
                            {[...SORT_OPTIONS, ...(isSupport ? SUPPORT_SORT_OPTIONS : [])].map(({ value, label }) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {/* Rejection Reason Filter */}
                {isSupport && displayRejectionReason && (
                    <Box mt={2}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="rejection-reason-label">Rejection Reason</InputLabel>
                            <Select
                                labelId="rejection-reason-label"
                                id="rejectionReason"
                                value={Array.isArray(filters.rejectionReason) ? filters.rejectionReason[0] || 'all' : filters.rejectionReason}
                            onChange={(e) => onFilterChange("rejectionReason", e.target.value)}
                                input={<OutlinedInput label="Rejection Reason" />}
                                MenuProps={MenuProps}
                        >
                                <MenuItem value="all">All</MenuItem>
                            {filterOptions['rejection_reasons']?.map((rejectionReason) => (
                                    <MenuItem key={rejectionReason.id} value={rejectionReason.id}>
                                    <Box>
                                        <Typography fontWeight={'bold'}>
                                            {rejectionReason.rejection_category} - {rejectionReason.rejection_reason}
                                        </Typography>
                                        <Typography
                                            fontSize={'small'}
                                            color="textSecondary"
                                            sx={{ textWrap: 'wrap' }}
                                        >
                                            {rejectionReason.rejection_description}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button variant="outlined" color="primary" onClick={onResetFilters}>
                        Reset
                    </Button>
                    <Button variant="contained" color="primary" onClick={onApplyFilters}>
                        Apply
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

// Helper function to parse URL params that might be arrays
const parseUrlParam = (param) => {
    if (!param || param === 'all') return ['all'];
    return param.split(',');
};

const createAppliedFilters = (filters) => {
    return Object.entries(filters)
        .filter(([key, value]) => {
            if (Array.isArray(value)) {
                return value.length > 0 && !value.includes('all');
            } else if (key === 'sort'){
                return value !== 'default';
            }
            return value !== 'all' && value !== '';
        })
        .map(([key, value]) => ({
            key,
            value: Array.isArray(value) ? value.join(', ') : value
        }));
};

const FilterBar = ({ displaySort = true, displayRejectionReason = false }) => {
	const { user } = useAuth();
	const userRoles = user.role;
    const isSupport = userRoles.includes('support');
    const isPartner = userRoles.includes('partner');

    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState({
        ...(isSupport && {
            source: parseUrlParam(searchParams.get('source')),
            campaign: parseUrlParam(searchParams.get('campaign')),
        }),
        state: parseUrlParam(searchParams.get('state')),
        priority: parseUrlParam(searchParams.get('priority')),
        dispositions: parseUrlParam(searchParams.get('dispositions')),
        sort: searchParams.get('sort') ?? 'default',
        ...(displayRejectionReason && {
            rejectionReason: searchParams.get('rejectionReason') ?? 'all'
        }),
    });
    const [modalOpen, setModalOpen] = useState(false);

    const searchBy = searchParams.get('search_by') ?? 'phone';
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search_term') ?? '');

    // The field updates on every keystroke, the URL does not: each URL write
    // re-runs the section's fetch effect, so typing a ten-digit number
    // unthrottled would fire ten requests and race their responses.
    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearchParams((searchParams) => {
                if (searchTerm.trim()) {
                    searchParams.set('search_term', searchTerm.trim());
                    searchParams.set('search_by', searchBy);
                } else {
                    searchParams.delete('search_term');
                }
                return searchParams;
            });
        }, 500);

        return () => clearTimeout(timeout);
    }, [searchTerm, searchBy, setSearchParams]);

    const handleFilterChange = (key, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value
        }));
    };

    const [appliedFilters, setAppliedFilters] = useState(createAppliedFilters(filters));

    const handleApplyFilters = () => {
        setSearchParams((searchParams) => {
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    searchParams.set(key, value.join(','));
                } else {
                    searchParams.set(key, value);
                }
            });
            return searchParams;
        });
        setModalOpen(false);
        setAppliedFilters(createAppliedFilters(filters));
    };

    // Clearing empties the URL as well as the form: leaving a stale `state=3`
    // in the query string while the modal shows "All" is the bug this avoids.
    // `alsoSearch` is what separates the modal's Reset, which owns only the
    // fields the modal shows, from the bar's Clear, which owns everything
    // narrowing the list.
    const clearFilters = ({ alsoSearch }) => {
        const cleared = Object.fromEntries(
            Object.entries(filters).map(([key, value]) => [
                key,
                Array.isArray(value) ? ['all'] : (key === 'sort' ? 'default' : 'all'),
            ])
        );

        setFilters(cleared);
        if (alsoSearch) setSearchTerm('');
        setSearchParams((searchParams) => {
            Object.keys(cleared).forEach((key) => searchParams.delete(key));
            if (alsoSearch) searchParams.delete('search_term');
            return searchParams;
        });
        setAppliedFilters([]);
        setModalOpen(false);
    };

    const handleResetFilters = () => clearFilters({ alsoSearch: false });
    const handleClearAll = () => clearFilters({ alsoSearch: true });

    const hasSomethingToClear = appliedFilters.length > 0 || searchTerm.trim().length > 0;

    return (
        <Box display="flex" alignItems="center" gap={2} overflow={'auto'} width={'100%'}>
            <Box display="flex" gap={1} alignItems="center" flexShrink={0}>
                <TextField
                    select
                    size="small"
                    value={searchBy}
                    onChange={(e) => setSearchParams((searchParams) => {
                        searchParams.set('search_by', e.target.value);
                        return searchParams;
                    })}
                    sx={{ minWidth: 130 }}
                >
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="enquiry_id">Enquiry ID</MenuItem>
                </TextField>

                <TextField
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={searchBy === 'phone' ? 'Phone number' : 'Enquiry ID'}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 200 }}
                />
            </Box>

            {/* The chips sit between the search box and the buttons and take the
                slack, so the two ends of the bar stay put however many are
                applied. */}
            <Box
                display="flex"
                gap={1}
                overflow={'auto'}
                flexGrow={1}
                justifyContent="flex-end"
            >
                {appliedFilters.length > 0 ? (
                    appliedFilters.map(({ key, value }) => (
                        <Chip key={key} label={`${key}: ${value}`} />
                    ))
                ) : (
                    <Typography>No filters applied</Typography>
                )}

                {/* Reset reads as one more chip rather than a third button: it
                    belongs to the chips it clears, and with nothing applied
                    there is nothing for it to do, so it is absent rather than
                    disabled. */}
                {hasSomethingToClear && (
                    <Chip
                        icon={<RestartAltIcon />}
                        label="Reset"
                        onClick={handleClearAll}
                        variant="outlined"
                        color="primary"
                    />
                )}
            </Box>

            <Button variant="contained" color="primary" onClick={() => setModalOpen(true)}>
                Filters
            </Button>
            <FilterModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                filters={filters}
                onFilterChange={handleFilterChange}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                isSupport={isSupport}
                isPartner={isPartner}
                displaySort={displaySort}
                displayRejectionReason={displayRejectionReason}
            />
        </Box>
    );
};

export default FilterBar;